import { Head, useAuth } from "zudoku/components";
import { useState } from "react";

export const SupportContactPage = () => {
  const auth = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Extract email, name, and tier from auth user object and/or decoded JWT token
  const getTokenPayload = () => {
    const token = (auth as any).providerData?.accessToken || auth.user?.access_token;
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  };

  const tokenPayload = auth.isAuthenticated ? getTokenPayload() : null;
  console.log("[SupportContactPage] auth:", auth);

  const userEmail =
    (auth as any).profile?.email ||
    auth.user?.email ||
    tokenPayload?.email ||
    "";
  const userName =
    (auth as any).profile?.name ||
    auth.user?.name ||
    tokenPayload?.name ||
    userEmail;
  const userTier =
    tokenPayload?.["https://api.securitydatapoint.com/tier"] ||
    tokenPayload?.tier ||
    null;
  const isDataSampler = userTier === "data-sampler";
  const hasSupportAccess = auth.isAuthenticated;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      // Get the auth token
      const token = 
        (auth as any).providerData?.accessToken ||
        auth.user?.access_token || 
        auth.user?.accessToken || 
        auth.accessToken;

      // Call Zuplo API endpoint for sending support email
      const response = await fetch(
        "https://api.securitydatapoint.com/api/support/contact",
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subject,
            message,
            userEmail,
            userName,
          }),
        }
      );

      if (response.ok) {
        setSuccess(true);
        setSubject("");
        setMessage("");
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "Failed to send message. Please try again.");
      }
    } catch (err) {
      setError(`Error: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="max-w-3xl mx-auto px-4 py-12">
      <Head>
        <title>Contact Support - SDP Developer Portal</title>
      </Head>
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-4">Contact Support</h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Need help? Send us a message and we'll get back to you as soon as possible.
        </p>
      </div>

      {/* User Info Display */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Logged in as: <span className="font-medium text-gray-900 dark:text-gray-100">{userEmail}</span>
        </p>
        {userTier && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Current tier: <span className="font-medium text-gray-900 dark:text-gray-100">{userTier}</span>
          </p>
        )}
      </div>

      {/* Data-Sampler promotional banner */}
      {isDataSampler && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            &#9888; Access to support is a temporary promotion for the Data-Sampler tier, and it may end in the future.
          </p>
        </div>
      )}

      {/* Show contact form for all authenticated users */}
      {hasSupportAccess && (
        <>
          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
                Message Sent Successfully
              </h3>
              <p className="text-sm text-green-800 dark:text-green-200">
                We've received your message and will respond to {userEmail}.
              </p>
            </div>
          )}

      {/* Contact Form */}
      <div className="p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Subject */}
          <div>
            <label 
              htmlFor="subject" 
              className="block text-sm font-medium mb-2"
            >
              Subject
            </label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief description of your issue"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              disabled={loading || !auth.isAuthenticated}
              required
            />
          </div>

          {/* Message */}
          <div>
            <label 
              htmlFor="message" 
              className="block text-sm font-medium mb-2"
            >
              Message
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe your issue or question in detail..."
              rows={8}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-vertical"
              disabled={loading || !auth.isAuthenticated}
              required
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !auth.isAuthenticated}
            className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
        </>
      )}

      {/* Additional Support Information */}
      <div className="mt-8 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h3 className="text-lg font-semibold mb-2 text-blue-900 dark:text-blue-100">
          Other Ways to Get Help
        </h3>
        <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
          <li>• Check our <a href="/articles" className="underline hover:text-blue-600 dark:hover:text-blue-300">documentation</a> for common questions</li>
          <li>• Review <a href="/articles/api-error-codes" className="underline hover:text-blue-600 dark:hover:text-blue-300">API error codes</a> for troubleshooting</li>
        </ul>
      </div>
    </section>
  );
};
